import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProfileModal from './profileModal';
import { ONBOARDING_FINAL_STEP } from '../../../../config';
import ProfileReportDownload from './profileDownloadReport';
import { fetchAllOnboardingProfiles } from '../../../adminSlice';
import CopyButton from '../../../../components/CopyButton';

const PROFILES_PER_PAGE = 10;

function OnboardingProfiles() {
    const dispatch = useDispatch();
    const profiles = useSelector(state => state.admin.profiles);
    const token = useSelector(state => state.admin.token);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalProfile, setModalProfile] = useState();
    const [expandedRows, setExpandedRows] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    // Lazy fetch — only fires when this page actually mounts. Was previously
    // dispatched eagerly from AdminHome regardless of which page the user was on.
    useEffect(() => {
        if (token) dispatch(fetchAllOnboardingProfiles());
    }, [dispatch, token]);

    const profileIds = Object.keys(profiles || {});
    const totalPages = Math.max(1, Math.ceil(profileIds.length / PROFILES_PER_PAGE));
    // Clamp during render, not in an effect: when the profile list shrinks while the
    // user sits on a later page, an effect-based clamp leaves one frame showing an
    // empty table and a nonsense "Showing 21–5 of 5" range.
    const safePage = Math.min(currentPage, totalPages);
    const firstProfileIndex = (safePage - 1) * PROFILES_PER_PAGE;
    const visibleProfileIds = profileIds.slice(firstProfileIndex, firstProfileIndex + PROFILES_PER_PAGE);

    useEffect(() => {
        setCurrentPage(page => Math.min(page, totalPages));
    }, [totalPages]);

    const toggleRow = id => {
        setExpandedRows(previous => ({ ...previous, [id]: !previous[id] }));
    };

    const getStatusTag = status => {
        const normalizedStatus = status || 'PENDING';
        const styles = normalizedStatus === 'REJECTED'
            ? 'bg-red-50 text-red-700 ring-red-600/20'
            : normalizedStatus === 'APPROVED'
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
                : 'bg-blue-50 text-blue-700 ring-blue-600/20';

        return (
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide ring-1 ring-inset ${styles}`}>
                {normalizedStatus}
            </span>
        );
    };

    const getProgressPercentage = currentStep => {
        const step = Number(currentStep);
        if (!Number.isFinite(step) || step <= 1) return 0;
        return Math.min(Math.round(step / ONBOARDING_FINAL_STEP * 100), 100);
    };

    const handleView = profile => {
        setModalProfile(profile);
        setIsModalOpen(true);
    };

    const formatDateTimeString = dateTimeString => {
        const date = new Date(dateTimeString);
        if (!(date instanceof Date) || isNaN(date)) return '- -';
        return date.toLocaleString('en-US', {
            year: 'numeric', month: 'long', day: '2-digit', hour: '2-digit',
            minute: '2-digit', second: '2-digit', hour12: false,
            timeZone: 'America/New_York',
        });
    };

    const renderProgress = profile => {
        const progress = getProgressPercentage(profile?.entry_point?.current_step);
        return (
            <div className="min-w-[104px]">
                <div className="mb-1.5">
                    <span className="text-sm font-medium text-blue-700">{progress}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>
        );
    };

    // Three fields per row on desktop instead of one long stacked column — the row
    // spans the whole table width, so a single column left most of it empty.
    // Plain-text summary for the "Copy details" button — every field in one paste.
    const buildHrReviewText = profile => {
        const review = profile?.hr_review || {};
        return [
            `Profile ID: #${profile?.profile?.id ?? '- -'}`,
            `Link Creator: ${profile?.token_creator || '- -'}`,
            `Status: ${review.status || '- -'}`,
            `Review By: ${review.admin?.username || '- -'}`,
            `Cost Center Name: ${review.cost_center_name || '- -'}`,
            `Cost Center ID: ${review.cost_center_id || '- -'}`,
            `Notes: ${review.notes || '- -'}`,
            `Updated At: ${formatDateTimeString(review.updated_on)}`,
        ].join('\n');
    };

    const renderExpandedDetails = profile => (
        <div className="text-sm text-slate-700">
            <div className="mb-3 flex items-center justify-between gap-3">
                <div className="font-bold text-[#0a1445]">HR Team Review</div>
                <CopyButton value={buildHrReviewText(profile)} label="HR review details" text="Copy details" />
            </div>
            <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-2">
                    <dt className="font-semibold">Status:</dt>
                    <dd>{getStatusTag(profile?.hr_review?.status)}</dd>
                </div>
                <div className="flex items-start gap-2">
                    <dt className="font-semibold whitespace-nowrap">Review By:</dt>
                    <dd className="flex min-w-0 items-start gap-1 break-words">{profile?.hr_review?.admin?.username || '- -'}<CopyButton value={profile?.hr_review?.admin?.username} label="Reviewer" /></dd>
                </div>
                <div className="flex items-start gap-2">
                    <dt className="font-semibold whitespace-nowrap">Cost Center Name:</dt>
                    <dd className="flex min-w-0 items-start gap-1 break-words">{profile?.hr_review?.cost_center_name || '- -'}<CopyButton value={profile?.hr_review?.cost_center_name} label="Cost Center Name" /></dd>
                </div>
                <div className="flex items-start gap-2">
                    <dt className="font-semibold whitespace-nowrap">Cost Center ID:</dt>
                    <dd className="flex min-w-0 items-start gap-1 break-words">{profile?.hr_review?.cost_center_id || '- -'}<CopyButton value={profile?.hr_review?.cost_center_id} label="Cost Center ID" /></dd>
                </div>
                <div className="flex items-start gap-2 sm:col-span-2">
                    <dt className="font-semibold whitespace-nowrap">Notes:</dt>
                    <dd className="flex min-w-0 items-start gap-1 break-words">{profile?.hr_review?.notes || '- -'}<CopyButton value={profile?.hr_review?.notes} label="Notes" /></dd>
                </div>
                <div className="flex items-start gap-2">
                    <dt className="font-semibold whitespace-nowrap">Updated At:</dt>
                    <dd className="min-w-0 break-words">{formatDateTimeString(profile?.hr_review?.updated_on)}</dd>
                </div>
            </dl>
        </div>
    );

    const renderActions = (id, profile, mobile = false) => (
        <div className={mobile ? 'flex items-center gap-2' : 'flex items-center justify-end gap-2 whitespace-nowrap'}>
            <button
                type="button"
                className={`${mobile ? 'flex-1 justify-center' : 'w-[116px] justify-center'} inline-flex h-9 items-center gap-2 rounded-lg bg-[#fb6635] px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#e95729] focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2`}
                onClick={() => handleView(profile)}
            >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
                Review
            </button>
            {/* The download button only exists once a profile is approved. Its slot is
                always reserved (same h-9 w-9 box) so Review and the expand chevron line
                up down the column instead of shifting row by row. */}
            {profile?.hr_review?.status === 'APPROVED' ? (
                <ProfileReportDownload profileId={profile?.profile?.id} />
            ) : (
                !mobile && <span className="h-9 w-9 flex-none" aria-hidden="true" />
            )}
                <button
                    type="button"
                    onClick={() => toggleRow(id)}
                    className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white/70 hover:text-[#211b4f] focus:outline-none focus:ring-2 focus:ring-[#211b4f] focus:ring-offset-2"
                    aria-expanded={Boolean(expandedRows[id])}
                    aria-label={expandedRows[id] ? 'Hide HR review details' : 'Show HR review details'}
                >
                    <svg
                        className={`h-5 w-5 transition-transform ${expandedRows[id] ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m6 9 6 6 6-6" />
                    </svg>
                </button>
        </div>
    );

    return (
        <div className="min-w-0 grow bg-[#f2ece9]">
            <div className="space-y-5 p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight text-[#0a1445] sm:text-3xl">Onboarding Profiles</h2>
                        <p className="mt-2 text-sm text-slate-600">Onboarding Profiles for review.</p>
                    </div>
                    {profileIds.length > 0 && (
                        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-[#211b4f] shadow-sm">
                            <span className="h-2 w-2 rounded-full bg-[#fb6635]" />
                            {profileIds.length} profiles
                        </div>
                    )}
                </div>
                <section className="overflow-hidden rounded-xl bg-transparent">
                            <div className="hidden overflow-x-auto md:block">
                                <table className="w-full min-w-[1040px] border-separate border-spacing-y-2 text-left text-sm text-[#211b4f]">
                                    <thead className="bg-[#211b4f] text-[11px] font-semibold uppercase tracking-wider text-white">
                                        <tr>
                                            <th scope="col" className="border-r border-white/20 px-5 py-3.5">ID</th>
                                            <th scope="col" className="border-r border-white/20 px-5 py-3.5">Link Creator</th>
                                            <th scope="col" className="border-r border-white/20 px-5 py-3.5">Progress</th>
                                            <th scope="col" className="border-r border-white/20 px-5 py-3.5">Manager</th>
                                            <th scope="col" className="border-r border-white/20 px-5 py-3.5">HR Review</th>
                                            <th scope="col" className="w-[320px] px-5 py-4 text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleProfileIds.map((id, index) => {
                                            const profile = profiles[id];
                                            return (
                                                <React.Fragment key={id}>
                                                    <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#eadfda]'} transition-colors hover:bg-[#fff7f2]`}>
                                                        <th scope="row" className="px-5 py-4">
                                                            <span className="inline-flex items-center gap-1 font-bold text-[#0a1445]">#{profile?.profile?.id}<CopyButton value={profile?.profile?.id} label="Profile ID" /></span>
                                                        </th>
                                                        <td className="max-w-[260px] px-5 py-4 font-medium" title={profile?.token_creator || 'NA'}><span className="flex max-w-[220px] items-center gap-1"><span className="min-w-0 truncate">{profile?.token_creator || 'NA'}</span><CopyButton value={profile?.token_creator} label="Email" /></span></td>
                                                        <td className="px-5 py-4">{renderProgress(profile)}</td>
                                                        <td className="px-5 py-4 font-medium">{profile?.profile?.data?.manager?.firstName || '- -'}</td>
                                                        <td className="px-5 py-4">{getStatusTag(profile?.hr_review?.status)}</td>
                                                        <td className="w-[320px] px-5 py-4">{renderActions(id, profile)}</td>
                                                    </tr>
                                                    {expandedRows[id] && (
                                                        <tr className="bg-[#fff7f2]">
                                                            <td colSpan="6" className="border-t border-orange-100 px-6 py-5">{renderExpandedDetails(profile)}</td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="grid gap-3 md:hidden">
                                {visibleProfileIds.map(id => {
                                    const profile = profiles[id];
                                    return (
                                        <article key={id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                                            <div className="p-4">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">ID</p>
                                                        <h2 className="mt-1 flex items-center gap-1 text-lg font-bold text-[#0a1445]">#{profile?.profile?.id}<CopyButton value={profile?.profile?.id} label="Profile ID" /></h2>
                                                    </div>
                                                    {getStatusTag(profile?.hr_review?.status)}
                                                </div>
                                                <div className="mt-4 border-y border-slate-100 py-4">
                                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Link Creator</p>
                                                    <p className="mt-1 flex items-start gap-1 break-all text-sm font-medium text-slate-700">{profile?.token_creator || 'NA'}<CopyButton value={profile?.token_creator} label="Email" /></p>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 py-4">
                                                    <div>
                                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Manager</p>
                                                        <p className="mt-1 text-sm font-semibold text-slate-700">{profile?.profile?.data?.manager?.firstName || '- -'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Progress</p>
                                                        <div className="mt-1">{renderProgress(profile)}</div>
                                                    </div>
                                                </div>
                                                {renderActions(id, profile, true)}
                                            </div>
                                            {expandedRows[id] && (
                                                <div className="border-t border-slate-200 bg-slate-50 px-4 py-4">
                                                    {renderExpandedDetails(profile)}
                                                </div>
                                            )}
                                        </article>
                                    );
                                })}
                            </div>

                    {profileIds.length > 0 && (
                        <nav className="mt-2 flex flex-col gap-3 rounded-xl bg-[#e7dfdc] px-4 py-4 sm:flex-row sm:items-center sm:justify-between" aria-label="Onboarding profiles pagination">
                            <p className="text-center text-sm text-slate-500 sm:text-left" aria-live="polite">
                                Showing <span className="font-semibold text-slate-700">{Math.min(firstProfileIndex + 1, profileIds.length)}–{Math.min(firstProfileIndex + PROFILES_PER_PAGE, profileIds.length)}</span> of {profileIds.length}
                            </p>
                            <div className="flex items-center justify-between gap-2 sm:justify-end">
                                <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40" onClick={() => setCurrentPage(Math.max(1, safePage - 1))} disabled={safePage === 1}>Previous</button>
                                <span className="whitespace-nowrap px-1 text-sm text-slate-500">{safePage} / {totalPages}</span>
                                <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40" onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))} disabled={safePage === totalPages}>Next</button>
                            </div>
                        </nav>
                    )}
                </section>
            </div>

            {isModalOpen && (
                <ProfileModal modalProfile={modalProfile} handleModalClose={() => setIsModalOpen(false)} />
            )}
        </div>
    );
}

export default OnboardingProfiles;
